import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, animate } from 'framer-motion';
import { Music, Sparkles, Users } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Word-by-word blur-to-focus animation
const TitleReveal = ({ text, className }) => {
  const words = text.split(' ');
  return (
    <motion.h2
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      variants={{
        visible: { transition: { staggerChildren: 0.12 } },
      }}
    >
      {words.map((word, i) => (
        <motion.span
          key={`${word}-${i}`}
          className="inline-block mr-[0.25em]"
          variants={{
            hidden: { opacity: 0, y: 18, filter: 'blur(10px)' },
            visible: {
              opacity: 1,
              y: 0,
              filter: 'blur(0px)',
              transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
            },
          }}
        >
          {word}
        </motion.span>
      ))}
    </motion.h2>
  );
};

// Extract numeric portion of a stat value ("5000", "16+", "8h", "1")
const parseStatValue = (raw) => {
  if (raw === null || raw === undefined) return { number: 0, prefix: '', suffix: '' };
  const str = String(raw);
  const match = str.match(/(\D*)(\d+)(.*)/);
  if (!match) return { number: 0, prefix: '', suffix: str };
  return { number: parseInt(match[2], 10), prefix: match[1] || '', suffix: match[3] || '' };
};

// Count-up animated stat
const AnimatedStat = ({ value, label, delay, inView }) => {
  const { number, prefix, suffix } = parseStatValue(value);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, number, {
      duration: 1.6,
      delay,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [inView, number, delay]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="text-center py-4"
    >
      <div className="font-display text-4xl sm:text-5xl text-[#C9A84C] mb-1">
        {prefix}
        {display}
        {suffix}
      </div>
      <div className="text-xs uppercase tracking-widest text-[#B9B2A8]">{label}</div>
    </motion.div>
  );
};

// Feature card with floating icon
const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ delay: index * 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.03 }}
      className="group bg-white/5 border border-white/10 hover:border-[#C9A84C]/50 rounded-2xl p-6 text-center transition-colors duration-300"
      style={{ willChange: 'transform' }}
    >
      <motion.div
        className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#C9A84C]/20 flex items-center justify-center group-hover:bg-[#C9A84C]/30 transition-colors"
        animate={{ y: [0, -4, 0] }}
        transition={{
          duration: 3 + index * 0.4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: index * 0.3,
        }}
      >
        <Icon className="w-6 h-6 text-[#C9A84C]" />
      </motion.div>
      <h3 className="font-display text-xl text-[#F5F0E8] mb-2">{feature.title}</h3>
      <p className="text-sm text-[#B9B2A8]">{feature.description}</p>
    </motion.div>
  );
};

const ExperienceSection = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    years: '16+',
    people: '5000',
    hours: '8h',
    one_place: '1',
  });

  const sectionRef = useRef(null);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: '-80px' });

  // Parallax on background image
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.2]);

  const [heroBg, setHeroBg] = useState('/images/experience-bg.jpg');

  useEffect(() => {
    // Admin-uploaded hero bg comes from /api/experience
    axios.get(`${BACKEND_URL}/api/experience`).then((r) => {
      const raw = r.data?.hero_bg_url;
      if (raw) {
        // Inline the getImageUrl rule (absolute URL vs /uploads/)
        const resolved = /^https?:\/\//i.test(raw) ? raw : `${BACKEND_URL}${raw}`;
        setHeroBg(resolved);
      }
    }).catch(() => {});

    const fetchContent = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/content`);
        if (response.data) {
          setStats({
            years: response.data.stats_years || '18+',
            people: response.data.stats_people || '5000',
            hours: response.data.stats_hours || '8h',
            one_place: '1',
          });
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  const features = [
    { icon: Music, title: t('experience.music'), description: t('experience.music_desc') },
    { icon: Sparkles, title: t('experience.decor'), description: t('experience.decor_desc') },
    { icon: Users, title: t('experience.community'), description: t('experience.community_desc') },
  ];

  const statsDisplay = [
    { value: stats.years, label: t('experience.years') },
    { value: stats.people, label: t('experience.people') },
    { value: stats.hours, label: t('experience.hours') },
    { value: stats.one_place, label: t('experience.one_place') },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-16 sm:py-24 bg-[#060606] relative overflow-hidden"
      data-testid="experience-section"
    >
      {/* Parallax Background Image */}
      <motion.div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          y: bgY,
          scale: bgScale,
          willChange: 'transform',
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#060606] via-[#060606]/80 to-[#060606]" />

      {/* Radial gold glow animation */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        animate={{
          background: [
            'radial-gradient(circle at 30% 20%, rgba(201,168,76,0.08), transparent 50%)',
            'radial-gradient(circle at 70% 80%, rgba(201,168,76,0.08), transparent 50%)',
            'radial-gradient(circle at 30% 20%, rgba(201,168,76,0.08), transparent 50%)',
          ],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <TitleReveal
            text={t('experience.title')}
            className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F0E8] mb-3"
          />
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ delay: 0.5, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif italic text-lg text-[#C9A84C]"
          >
            {t('experience.subtitle')}
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <FeatureCard key={feature.title} feature={feature} index={index} />
          ))}
        </div>

        {/* Stats */}
        <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {statsDisplay.map((stat, index) => (
            <AnimatedStat
              key={stat.label}
              value={stat.value}
              label={stat.label}
              delay={index * 0.1}
              inView={statsInView}
            />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link to="/experience">
            <Button
              size="lg"
              variant="outline"
              className="border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C] font-display uppercase tracking-wider transition-all duration-300"
              data-testid="experience-cta"
            >
              Découvrir l'expérience →
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ExperienceSection;
